/**
 * IntegrationsSettings Component
 *
 * Displays integration configuration options:
 * - Email notifications for new leads
 * - Webhook integration for CRM systems
 */

import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { useTenantCompat as useTenant } from '@/hooks/api';
import { WebhookConfig } from '@/components/settings/WebhookConfig';
import { EmailNotificationConfig } from '@/components/settings/EmailNotificationConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Webhook } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

export default function IntegrationsSettings() {
  const { tenantId } = useAuthContext();
  const { user } = useUser();
  const { data: tenant, isLoading, refetch } = useTenant(tenantId ?? undefined);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Get owner email - prefer tenant owner's email, fallback to current user
  const ownerEmail = user?.primaryEmailAddress?.emailAddress;

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Get notified instantly when new leads are submitted
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tenant && tenantId && (
            <EmailNotificationConfig
              tenantId={tenantId}
              emailNotificationsEnabled={tenant.email_notifications_enabled !== false}
              ownerEmail={ownerEmail}
              onUpdate={refetch}
            />
          )}
        </CardContent>
      </Card>

      {/* Webhook Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            Webhook Integration
          </CardTitle>
          <CardDescription>
            Receive lead data directly to your CRM or any external system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tenant && tenantId && (
            <WebhookConfig
              tenantId={tenantId}
              webhookUrl={tenant.webhook_url || ''}
              webhookEnabled={tenant.webhook_enabled || false}
              onUpdate={refetch}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
